/*!
 * Dr.Golf Swing Assessment — CONFIGURATION
 * ------------------------------------------------------------------
 * Everything a non-developer is likely to edit lives here:
 *   - the 10 questions, answer copy, weights and evidence statements
 *   - Dr.Golf "reaction" moments between questions
 *   - recommendation thresholds
 *   - store, product, offer and analytics settings
 *
 * Weights use five hidden dimensions:
 *   T Tempo / Sequencing   C Connection / Coordination
 *   P Pressure Sensitivity E Competitive Effort   O Overthinking / Tension
 *
 * Per-option fields:
 *   w        dimension points awarded when chosen
 *   contra   dimensions this answer argues AGAINST (used for evidence strength)
 *   evidence what we may say back to the golfer ("You told us…"). Only ever
 *            shown when this exact option was selected.
 * ------------------------------------------------------------------ */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else { root.DrGolf = root.DrGolf || {}; root.DrGolf.config = factory(); }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var questions = [
    {
      id: 'q1', type: 'technical', eyebrow: 'Primary frustration',
      why: 'Your biggest frustration tells us where to start looking. There’s no wrong answer.',
      text: 'When your swing breaks down, what frustrates you most?',
      options: [
        { id: 'A', label: 'My contact is unpredictable—pure one swing, fat or thin the next.', w: { T: 1, C: 1 },
          evidence: 'Your biggest frustration is unpredictable contact—pure one swing, fat or thin the next.' },
        { id: 'B', label: 'I get quick or rushed and completely lose my rhythm.', w: { T: 3 },
          evidence: 'When your swing breaks down, you said you get quick and lose your rhythm.' },
        { id: 'C', label: 'My arms or hands seem to take over instead of working with my body.', w: { C: 3 },
          evidence: 'When your swing breaks down, you said your arms or hands take over from your body.' },
        { id: 'D', label: 'I can hit great shots; I just can’t repeat them consistently.', w: { T: 1, C: 1 },
          evidence: 'You can hit great shots—your frustration is repeating them.' },
        { id: 'E', label: 'Everything gets worse when I try to add distance.', w: { T: 2, C: 1, E: 1 },
          evidence: 'You told us things get worse when you try to add distance.' }
      ]
    },
    {
      id: 'q2', type: 'technical', eyebrow: 'Ball striking',
      why: 'Contact patterns are useful clues, but on their own they don’t point to a single cause. We read this alongside your other answers.',
      text: 'Which contact pattern sounds most familiar on a bad day?',
      options: [
        { id: 'A', label: 'Fat and thin shots both show up.', w: { T: 1, C: 1 },
          evidence: 'On a bad day, both fat and thin shots show up.' },
        { id: 'B', label: 'I catch the heel or toe more than I should.', w: { C: 1 },
          evidence: 'On a bad day, you catch the heel or toe more than you’d like.' },
        { id: 'C', label: 'Contact is decent, but direction and distance vary.', w: { T: 1 },
          evidence: 'Your contact holds up reasonably well, but direction and distance vary.' },
        { id: 'D', label: 'Strike isn’t my biggest problem—the swing feels mistimed.', w: { T: 3 },
          evidence: 'Strike isn’t your main issue—you said the swing itself feels mistimed.' },
        { id: 'E', label: 'Contact gets worse when I try to swing harder.', w: { T: 2, C: 1, E: 1 },
          evidence: 'Your contact gets worse when you try to swing harder.' }
      ]
    },
    {
      id: 'q3', type: 'technical', eyebrow: 'Transition',
      why: 'The transition is where many timing issues first show up.',
      text: 'From the top of your backswing, what usually happens?',
      options: [
        { id: 'A', label: 'It feels smooth and controlled; my problems happen later.', w: {}, contra: ['T'],
          evidence: 'Your transition from the top usually feels smooth and controlled.' },
        { id: 'B', label: 'I tend to get quick and start down aggressively.', w: { T: 3 },
          evidence: 'You told us you tend to get quick and start down aggressively from the top.' },
        { id: 'C', label: 'My arms feel like they fire before the rest of my body.', w: { C: 3 },
          evidence: 'From the top, your arms feel like they fire before the rest of your body.' },
        { id: 'D', label: 'My body starts turning and my arms feel left behind.', w: { T: 1, C: 3 },
          evidence: 'From the top, your body starts turning and your arms feel left behind.' },
        { id: 'E', label: 'It changes depending on the shot or the pressure.', w: { T: 1, P: 2 },
          evidence: 'Your transition changes depending on the shot or the pressure.' }
      ]
    },
    {
      id: 'q4', type: 'situational', eyebrow: 'Range vs. course',
      why: 'Comparing your range and course swings helps us separate mechanics from pressure.',
      text: 'How different is your swing on the course compared with the range?',
      options: [
        { id: 'A', label: 'Very similar. Pressure doesn’t change much.', w: {}, contra: ['P'],
          evidence: 'Your course swing stays close to your range swing.' },
        { id: 'B', label: 'A little quicker once the score matters.', w: { T: 2, P: 2 },
          evidence: 'Your swing gets a little quicker once the score matters.' },
        { id: 'C', label: 'I get noticeably tighter, especially in my hands and arms.', w: { P: 3, O: 1 },
          evidence: 'On the course, you get noticeably tighter in your hands and arms.' },
        { id: 'D', label: 'I start steering the ball instead of making a free swing.', w: { C: 1, P: 3 },
          evidence: 'On the course, you start steering the ball instead of swinging freely.' },
        { id: 'E', label: 'Some days my range swing seems to stay in the parking lot.', w: { T: 1, C: 1, P: 2 },
          evidence: 'Some days your range swing doesn’t make it to the first tee.' }
      ]
    },
    {
      id: 'q5', type: 'technical', eyebrow: 'Connection',
      why: 'This is our main question about arm–body coordination. If you’re not sure, say so—it won’t count for or against you.',
      text: 'Which description best matches your arms and body through the swing?',
      options: [
        { id: 'A', label: 'They generally feel synchronized.', w: {}, contra: ['C'],
          evidence: 'Your arms and body generally feel synchronized.' },
        { id: 'B', label: 'My arms sometimes separate from my chest and feel independent.', w: { C: 3 },
          evidence: 'Your arms sometimes separate from your chest and feel independent.' },
        { id: 'C', label: 'My body can stop while my hands and arms keep going.', w: { C: 3 },
          evidence: 'Your body can stop turning while your hands and arms keep going.' },
        { id: 'D', label: 'My body rotates hard and my arms struggle to catch up.', w: { C: 3 },
          evidence: 'Your body rotates hard and your arms struggle to catch up.' },
        // Uncertainty is NOT evidence of a connection fault — deliberately zero-weighted.
        { id: 'E', label: 'I’m not sure what my arms and body are doing relative to each other.', w: {},
          evidence: null }
      ]
    },
    {
      id: 'q6', type: 'technical', eyebrow: 'Your best swing',
      why: 'Your best swings are the reference point. We want to understand what changes when that feeling disappears.',
      text: 'Think about the shots you absolutely flush. What do those swings usually feel like?',
      options: [
        { id: 'A', label: 'Smooth and almost effortless.', w: { T: 2 }, echo: 'smooth and almost effortless',
          evidence: 'Your best swings feel smooth and almost effortless.' },
        { id: 'B', label: 'Like my arms and torso are moving together.', w: { C: 3 }, echo: 'arms and torso moving together',
          evidence: 'Your best swings feel like your arms and torso are moving together.' },
        { id: 'C', label: 'Surprisingly easy—I’m not manufacturing speed.', w: { T: 2, E: 1 }, echo: 'surprisingly easy',
          evidence: 'Your best swings feel easy—you’re not manufacturing speed.' },
        { id: 'D', label: 'Balanced from start to finish.', w: { T: 1, C: 1 }, echo: 'balanced from start to finish',
          evidence: 'Your best swings feel balanced from start to finish.' },
        { id: 'E', label: 'Like I stopped thinking and simply swung.', w: { T: 1, O: 2 }, echo: 'like you stopped thinking and simply swung',
          evidence: 'Your best swings happen when you stop thinking and simply swing.' }
      ]
    },
    {
      id: 'q7', type: 'technical', eyebrow: 'Added speed',
      why: 'Adding effort tends to expose whatever isn’t quite repeatable yet.',
      text: 'What happens when you consciously try to hit the ball harder?',
      options: [
        { id: 'A', label: 'Not much. My motion stays consistent.', w: {}, contra: ['T', 'C', 'E'],
          evidence: 'Your motion stays consistent when you try to hit it harder.' },
        { id: 'B', label: 'My transition gets noticeably faster.', w: { T: 3, E: 1 },
          evidence: 'When you try to hit it harder, your transition gets noticeably faster.' },
        { id: 'C', label: 'My hands and arms start dominating the swing.', w: { C: 3, E: 1 },
          evidence: 'When you try to hit it harder, your hands and arms start dominating.' },
        { id: 'D', label: 'My balance or swing shape starts falling apart.', w: { T: 1, C: 1, E: 1 },
          evidence: 'When you add effort, your balance or swing shape starts to fall apart.' },
        { id: 'E', label: 'Contact gets worse before I gain useful distance.', w: { T: 2, C: 1, E: 2 },
          evidence: 'When you add effort, contact suffers before you gain useful distance.' }
      ]
    },
    {
      id: 'q8', type: 'situational', eyebrow: 'Let’s be honest',
      why: 'Competitive instinct can change a swing without you deciding to. Be honest—nobody’s watching.',
      text: 'Your buddy just carried you by 15 yards. You’re next. What happens?',
      options: [
        { id: 'A', label: 'Nothing changes. Same swing.', w: {}, contra: ['E'],
          evidence: 'Getting outdriven doesn’t change your swing.' },
        { id: 'B', label: 'I probably give the next one a little extra.', w: { T: 1, E: 2 },
          evidence: 'After getting outdriven, you give the next one a little extra.' },
        { id: 'C', label: 'I’m absolutely trying to send the next ball into another postal code.', w: { T: 2, E: 3 },
          evidence: 'After getting outdriven, you’re trying to send the next one into another postal code.' },
        { id: 'D', label: 'I tell myself I’ll swing smoothly, but everything gets tighter.', w: { T: 1, P: 1, E: 1, O: 1 },
          evidence: 'You tell yourself to swing smoothly, but everything gets tighter.' },
        { id: 'E', label: 'I tell everyone I don’t care, which is usually evidence that I care.', w: { E: 2 },
          evidence: 'You say you don’t care when you get outdriven. (We noted it anyway.)' }
      ]
    },
    {
      id: 'q9', type: 'technical', eyebrow: 'Mental load',
      why: 'Mental load affects tension and rhythm more than most golfers expect.',
      text: 'When you stand over the ball, what’s normally happening in your head?',
      options: [
        { id: 'A', label: 'Target, setup, swing. Pretty simple.', w: {}, contra: ['O'],
          evidence: 'Over the ball, your thinking stays simple: target, setup, swing.' },
        { id: 'B', label: 'One or two mechanical thoughts.', w: { O: 1 },
          evidence: 'You usually carry one or two mechanical thoughts over the ball.' },
        { id: 'C', label: 'Several positions or movements I’m actively trying to control.', w: { O: 3 },
          evidence: 'Over the ball, you’re actively trying to control several positions or movements.' },
        { id: 'D', label: 'My swing thought changes depending on my last bad shot.', w: { O: 3, P: 1 },
          evidence: 'Your swing thought changes depending on your last bad shot.' },
        { id: 'E', label: 'I’ve watched enough golf instruction that several fixes are competing for attention.', w: { O: 3 },
          evidence: 'Several swing fixes compete for your attention over the ball.' }
      ]
    },
    {
      id: 'q10', type: 'situational', eyebrow: 'Pressure response',
      why: 'What changes physically under pressure helps us tailor your advice.',
      text: 'Narrow fairway. Trouble on both sides. This shot matters. What changes physically?',
      options: [
        { id: 'A', label: 'Very little. I make my normal swing.', w: {}, contra: ['P'],
          evidence: 'Under pressure, you make your normal swing.' },
        { id: 'B', label: 'My backswing or transition gets quicker.', w: { T: 2, P: 2 },
          evidence: 'When the shot matters, your backswing or transition gets quicker.' },
        { id: 'C', label: 'My grip and arms get noticeably tighter.', w: { P: 3, O: 1 },
          evidence: 'When the shot matters, your grip and arms get noticeably tighter.' },
        { id: 'D', label: 'I start guiding or steering the club through impact.', w: { C: 1, P: 3 },
          evidence: 'When the shot matters, you start guiding the club through impact.' },
        { id: 'E', label: 'I add effort because I really want to make sure I get through the shot.', w: { T: 1, P: 2, E: 2 },
          evidence: 'When the shot matters, you add effort to make sure you get through it.' }
      ]
    }
  ];

  /* Dr.Golf reactions — shown after the listed question, keyed by the option chosen.
     `*` is the fallback for any option not listed. {echo} is replaced with the option's echo text. */
  var reactions = {
    q3: {
      A: 'Good to know—a controlled transition narrows things down. Next, let’s look at how your arms and body work together.',
      E: 'A transition that changes with the situation is a useful clue. Next, let’s see whether this is mainly about timing or how your body and arms work together.',
      '*': 'The transition is an important clue. Next, let’s see whether this is mainly about timing or how your body and arms work together.'
    },
    q5: {
      E: 'That’s an honest answer—most golfers can’t see this without video. We won’t count it for or against you.'
    },
    q6: {
      '*': '“{echo}.” Good—your best swings give us something useful to compare against. Now let’s look at what changes when that feeling disappears.'
    },
    q8: {
      A: 'Same swing regardless of the scoreboard. That’s a genuinely useful trait—we’ll keep it in mind.',
      E: 'We appreciate the honesty. Competitive instinct can change how golfers approach a swing—even when they don’t intend to change their mechanics.',
      '*': 'Noted. Competitive instinct can change how golfers approach a swing—even when they don’t intend to change their mechanics.'
    }
  };

  /* Questions are grouped into three named parts (shown in the progress bar). Order is unchanged. */
  var chapters = [
    { id: 'I', title: 'Your pattern', short: 'Pattern', blurb: 'What goes wrong, how it shows up in your strike, and what happens at the top.', questions: ['q1', 'q2', 'q3'] },
    { id: 'II', title: 'Your swing', short: 'Swing', blurb: 'How your swing changes on the course, how your arms and body work together, and how your best swings feel.', questions: ['q4', 'q5', 'q6', 'q7'] },
    { id: 'III', title: 'When it counts', short: 'Pressure', blurb: 'Competition, mental load and pressure—the moments that change a swing.', questions: ['q8', 'q9', 'q10'] }
  ];

  /* Photography. Golf photos: Unsplash License (free for commercial use). Replace with your own shoot when available.
     Credits: Marvin Meyer (golfer-trees), Yianni Mathioudakis (tee-address), Ivan Aviles (grip), Mike Cox (golden-swing). */
  var imagery = {
    intro: { src: 'photo-golfer-trees.jpg', alt: 'Golfer in black finishing a full swing on a tree-lined fairway', w: 1400, h: 1750 },
    analysis: { src: 'photo-golden-swing.jpg', alt: 'Golfer swinging at sunset on an open course', w: 1600, h: 1067 },
    results: { src: 'photo-golden-swing.jpg', alt: 'Golfer swinging at sunset on an open course', w: 1600, h: 1067 },
    reactions: {
      q3: { src: 'photo-grip.jpg', alt: 'Close-up of two hands gripping a golf club', w: 1200, h: 1800 },
      q6: { src: 'photo-golden-swing.jpg', alt: 'Golfer swinging at sunset on an open course', w: 1600, h: 1067 },
      q8: { src: 'photo-tee-address.jpg', alt: 'Driver set behind a teed golf ball, golfer at address', w: 1200, h: 1800 }
    },
    chapters: {
      I: { src: 'photo-grip.jpg', alt: 'Close-up of two hands gripping a golf club', w: 1200, h: 1800 },
      II: { src: 'photo-golfer-trees.jpg', alt: 'Golfer in black finishing a full swing on a tree-lined fairway', w: 1400, h: 1750 },
      III: { src: 'photo-tee-address.jpg', alt: 'Driver set behind a teed golf ball, golfer at address', w: 1200, h: 1800 }
    }
  };

  /* Recommendation rules. These are INITIAL COMMERCIAL RULES, not validated diagnostic thresholds. */
  var engine = {
    leadMargin: 15,            // T must lead C (or vice versa) by this many normalized points
    combinedMin: 40,           // both T and C ≥ this AND within leadMargin → combined / bundle
    supportMinWeight: 2,       // an answer "supports" a family when it gives ≥ this many points to T or C
    minSupportForPattern: 2,   // a single-family pattern needs at least this many supporting answers
    strong: { support: 3, technical: 2, maxContra: 1 },
    moderate: { support: 2 },
    levels: { moderate: 30, high: 60 },     // 0–29 low · 30–59 moderate · 60–100 high
    maintenance: { tcBelow: 30, othersBelow: 30, maxSupport: 1 },
    starterTiebreak: 'tempo',
    maxSecondary: 3,
    maxEvidence: 4
  };

  /* Product family → product key (see `products`). */
  var families = {
    tempo: { label: 'Tempo & Rhythm', products: ['tempo'] },
    connection: { label: 'Arm–Body Connection', products: ['band'] },
    combined: { label: 'Rhythm + Connection', products: ['tempo', 'band'] }
  };

  /* ---------- Commerce ----------
     Shopify stays the source of truth. Price, availability and variants are fetched live
     from the Storefront API (tokenless). Nothing here is displayed as a price unless
     Shopify returns it. Leave fields null rather than guessing. */
  var store = {
    domain: 'drgo1f.myshopify.com',       // permanent *.myshopify.com domain (or custom domain once connected)
    storefrontApiVersion: '2025-07',
    storefrontToken: '',                  // optional public Storefront token; tokenless access works without it
    // Follows the visitor's Shopify market (US or CA) so Storefront prices are in their own currency; CA outside Shopify.
    country: (typeof window !== 'undefined' && window.Shopify && /^[A-Z]{2}$/.test(window.Shopify.country || '')) ? window.Shopify.country : 'CA',
    language: 'EN',
    policies: {
      // Fill in once the policies exist in Shopify → Settings → Policies. While null, nothing is promised.
      refund: '/policies/refund-policy',
      shipping: '/policies/shipping-policy',
      privacy: 'https://drgo1f.myshopify.com/policies/privacy-policy'
    }
  };

  /* Image files live in assetBase. Inside a Shopify theme the section sets window.DrGolfAssetBase
     to the theme's asset folder, where these exact files already exist. */
  var assetBase = 'assets/img/';

  var products = {
    tempo: {
      // VERIFY: real product handle once the product is published to the Online Store / Headless channel.
      handle: 'tempo-swing-stick',
      name: 'Tempo Swing Stick',
      image: 'dr-golf-tempo-premium.jpg',
      imageAlt: 'Black Dr.Golf Tempo Swing Stick with molded grip and foam-covered weighted tip',
      lifestyleImage: 'dr-golf-tempo-practice.jpg',
      lifestyleAlt: 'Golfer at the top of a practice swing with the Tempo Swing Stick',
      storeUrl: null    // optional: full PDP URL on the main store; defaults to /products/{handle}
    },
    band: {
      handle: 'arm-connection-band',
      name: 'Connection Band',
      image: 'dr-golf-band-premium.jpg',
      imageAlt: 'Black Dr.Golf Connection Band',
      lifestyleImage: 'dr-golf-band-athlete.jpg',
      lifestyleAlt: 'Golfer wearing the Dr.Golf Connection Band around both upper arms, just above the elbows',
      storeUrl: null
    }
  };

  /* Consultation offer. Only shown when real values are set here AND exist in Shopify.
     discountCode must be an active Shopify discount — it is applied to the cart and Shopify
     validates it at checkout. Savings are never calculated or displayed by this page;
     Shopify shows the real discounted total at checkout. */
  var offer = {
    id: 'consult-v1',                     // reported to analytics + cart attributes
    discountCode: null,                   // SWING10 is deactivated; the live quiz (golf-quiz.js) uses GOLF10US / GOLF10 from section settings
    discountHeadline: null,
    bundleDiscountCode: null,             // used instead of discountCode when the bundle is in the cart
    bundleHeadline: null,                 // e.g. 'Save 15% when you train both feels'
    expiresAt: null,                      // ISO date string. When set, the offer disappears after this moment.
    freeShipping: null,                   // e.g. 'Free shipping on orders over $75 in Canada' — ONLY if configured in Shopify
    guarantee: '30-day returns and a 30-day swing guarantee',
    guideIncluded: true                   // the personalized practice protocol (this page + printable)
  };

  var analytics = {
    enabled: true,
    // false = no consent pop-up on this page. Inside Shopify, the store's own cookie/consent
    // settings (Customer Privacy API) are still respected. Set true to show the page's own banner.
    requireConsent: false,
    debug: false,
    gtag: true,       // pushes to window.dataLayer if present
    meta: true,       // fbq('trackCustom', …) if fbq present
    shopify: true     // Shopify.analytics.publish(…) when running inside a Shopify theme
  };

  var campaignParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'angle', 'ad_id', 'fbclid', 'gclid'];

  return {
    version: '1.0.0',
    questions: questions,
    reactions: reactions,
    chapters: chapters,
    imagery: imagery,
    engine: engine,
    families: families,
    store: store,
    products: products,
    assetBase: assetBase,
    offer: offer,
    analytics: analytics,
    campaignParams: campaignParams,
    storageKey: 'drgolf.assessment.v1',
    storageTtlDays: 14
  };
});
